/**
 * Offline Sync Service
 * Manages data synchronization between IndexedDB and server
 */
import axios from 'axios';
import { indexedDBService, STORES } from './indexedDBService';

class OfflineSyncService {
  constructor() {
    this.isOnline = navigator.onLine;
    this.syncInProgress = false;
    this.listeners = new Set();
    
    // Listen for online/offline events
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());
  }

  // Event handling
  handleOnline() {
    this.isOnline = true;
    this.notifyListeners({ type: 'online' });
    this.syncAll();
  }

  handleOffline() {
    this.isOnline = false;
    this.notifyListeners({ type: 'offline' });
  }

  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  notifyListeners(event) {
    this.listeners.forEach(callback => callback(event));
  }

  // Initialize service
  async init(apiUrl, token) {
    this.apiUrl = apiUrl;
    this.token = token;
    
    await indexedDBService.init();
    
    // Initial sync if online
    if (this.isOnline && token) {
      await this.syncAll();
    }
  }

  updateToken(token) {
    this.token = token;
  }

  getHeaders() {
    return {
      Authorization: `Bearer ${this.token}`,
      'Content-Type': 'application/json',
    };
  }

  // Sync all data
  async syncAll() {
    if (this.syncInProgress || !this.isOnline || !this.token) return;
    
    this.syncInProgress = true;
    this.notifyListeners({ type: 'sync_start' });

    try {
      // Process offline queue first
      await this.processOfflineQueue();
      
      // Then sync data from server
      await Promise.all([
        this.syncAudits(),
        this.syncTemplates(),
        this.syncCalendarEvents(),
      ]);

      this.notifyListeners({ type: 'sync_complete' });
    } catch (error) {
      console.error('Sync error:', error);
      this.notifyListeners({ type: 'sync_error', error });
    } finally {
      this.syncInProgress = false;
    }
  }

  // Process offline queue
  async processOfflineQueue() {
    const queue = await indexedDBService.getOfflineQueue();
    
    for (const item of queue) {
      try {
        const response = await axios({
          method: item.action,
          url: `${this.apiUrl}${item.endpoint}`,
          data: item.data,
          headers: this.getHeaders(),
        });

        // Success - remove from queue
        await indexedDBService.removeFromQueue(item.id);
        this.notifyListeners({ 
          type: 'queue_item_synced', 
          item,
          response: response.data 
        });
      } catch (error) {
        // Increment retry count
        item.retries++;
        
        if (item.retries >= 3) {
          // Max retries reached, remove and notify
          await indexedDBService.removeFromQueue(item.id);
          this.notifyListeners({ 
            type: 'queue_item_failed', 
            item,
            error 
          });
        } else {
          // Update retry count
          await indexedDBService.put(STORES.OFFLINE_QUEUE, item);
        }
      }
    }
  }

  // Sync audits from server
  async syncAudits() {
    try {
      const response = await axios.get(`${this.apiUrl}/audits`, {
        headers: this.getHeaders(),
        params: { limit: 100 }, // Get recent audits
      });

      if (response.data?.audits) {
        await indexedDBService.cacheAudits(response.data.audits);
        await indexedDBService.setLastSync('audits');
      }
    } catch (error) {
      console.error('Failed to sync audits:', error);
    }
  }

  // Sync templates from server
  async syncTemplates() {
    try {
      // Templates are typically static, so we might have a templates endpoint
      // For now, we'll cache the built-in templates
      const builtInTemplates = [
        { id: 'dementia_nursing', title: 'Dementia - Initial Nursing Assessment', lcd_type: 'dementia', discipline: 'rn' },
        { id: 'cancer_symptom', title: 'Cancer - Symptom Management Visit', lcd_type: 'cancer', discipline: 'rn' },
        { id: 'chf_assessment', title: 'CHF - Cardiac Assessment', lcd_type: 'chf', discipline: 'rn' },
        { id: 'msw_psychosocial', title: 'MSW - Psychosocial Assessment', lcd_type: 'general', discipline: 'msw' },
        { id: 'chaplain_spiritual', title: 'Chaplain - Spiritual Care Visit', lcd_type: 'general', discipline: 'chaplain' },
      ];
      
      await indexedDBService.cacheTemplates(builtInTemplates);
      await indexedDBService.setLastSync('templates');
    } catch (error) {
      console.error('Failed to sync templates:', error);
    }
  }

  // Sync calendar events from server
  async syncCalendarEvents() {
    try {
      const response = await axios.get(`${this.apiUrl}/calendar/events`, {
        headers: this.getHeaders(),
      });

      if (response.data?.events) {
        await indexedDBService.cacheCalendarEvents(response.data.events);
        await indexedDBService.setLastSync('calendar_events');
      }
    } catch (error) {
      console.error('Failed to sync calendar events:', error);
    }
  }

  // Queue action for offline
  async queueAction(action, endpoint, data) {
    await indexedDBService.queueOfflineAction(action, data, endpoint);
    this.notifyListeners({ type: 'action_queued', action, endpoint });
  }

  // Get cached data
  async getCachedAudits(userId) {
    return indexedDBService.getCachedAudits(userId);
  }

  async getCachedTemplates() {
    return indexedDBService.getCachedTemplates();
  }

  async getCachedCalendarEvents() {
    return indexedDBService.getCachedCalendarEvents();
  }

  // Get queue status
  async getQueueStatus() {
    const queue = await indexedDBService.getOfflineQueue();
    return {
      count: queue.length,
      items: queue,
    };
  }

  // Get storage info
  async getStorageInfo() {
    return indexedDBService.getStorageEstimate();
  }

  // Clear all cached data
  async clearAllCache() {
    await indexedDBService.clear(STORES.AUDITS);
    await indexedDBService.clear(STORES.TEMPLATES);
    await indexedDBService.clear(STORES.CALENDAR_EVENTS);
    await indexedDBService.clear(STORES.SYNC_META);
  }
}

// Singleton instance
const offlineSyncService = new OfflineSyncService();

export { offlineSyncService };
export default offlineSyncService;
